import React, { useState } from 'react';
import { 
  Plus, 
  Search, 
  ChevronRight, 
  ChevronDown, 
  FileText, 
  Calendar,
  History,
  Filter,
  Download,
  Trash2,
  Edit2
} from 'lucide-react';
import { JournalEntry, AccountingAccount } from '../../types';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { safeFormat } from '../../utils/dateUtils';
import { EntryFormModal } from './EntryFormModal';
import { deleteJournalEntry, repairReferenceCodes } from '../../services/accountingService';

interface JournalViewProps {
  entries: JournalEntry[];
  accounts: AccountingAccount[];
  userId: string;
}

export const JournalView: React.FC<JournalViewProps> = ({ entries, accounts, userId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<JournalEntry | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showUnbalancedOnly, setShowUnbalancedOnly] = useState(false);
  const [showNoRefOnly, setShowNoRefOnly] = useState(false);
  const [isRepairing, setIsRepairing] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(amount);
  };

  const getAccountName = (id: string) => {
    const acc = accounts.find(a => a.id === id);
    return acc ? `${acc.code} - ${acc.name}` : 'Cuenta desconocida';
  };

  const isEntryUnbalanced = (entry: JournalEntry) => {
    const totalDebit = entry.lines.reduce((sum, line) => sum + line.debit, 0);
    const totalCredit = entry.lines.reduce((sum, line) => sum + line.credit, 0);
    return Math.abs(totalDebit - totalCredit) > 0.01;
  };

  const filteredEntries = entries.filter(e => {
    const entryId = e.id.slice(-4).toUpperCase();
    const docCode = (e.documentLocatorCode || '').toUpperCase();
    const refSearch = `REF: ${docCode}`;
    const matchesSearch = e.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          entryId.includes(searchTerm.toUpperCase()) ||
                          docCode.includes(searchTerm.toUpperCase()) ||
                          refSearch.includes(searchTerm.toUpperCase()) ||
                          e.lines.some(l => getAccountName(l.accountId).toLowerCase().includes(searchTerm.toLowerCase()));
    
    let matches = matchesSearch;
    if (showUnbalancedOnly) {
      matches = matches && isEntryUnbalanced(e);
    }
    if (showNoRefOnly) {
      matches = matches && !e.documentLocatorCode;
    }
    return matches;
  });

  const handleDelete = async (id: string) => {
    try {
      await deleteJournalEntry(userId, id);
      setDeletingId(null);
    } catch (e) {
      console.error(e);
      alert("Error al eliminar el asiento.");
    }
  };

  const handleRepair = async () => {
    if (isRepairing) return;
    setIsRepairing(true);
    try {
      const count = await repairReferenceCodes(userId);
      alert(`Sincronización completada. Se repararon ${count} asientos.`);
    } catch (e) {
      console.error(e);
      alert("Error al reparar códigos de referencia.");
    } finally {
      setIsRepairing(false);
    }
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-3xl overflow-hidden shadow-2xl">
      <div className="p-6 border-b border-zinc-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
          <input 
            type="text"
            placeholder="Buscar en diario..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl pl-10 pr-4 py-2 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRepair}
            disabled={isRepairing}
            className="px-4 py-2 rounded-xl text-xs font-bold bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white transition-all border border-transparent disabled:opacity-50"
            title="Sincroniza y repara códigos de referencia faltantes"
          >
            {isRepairing ? 'Sincronizando...' : 'Sincronizar Refs'}
          </button>
          <button
            onClick={() => setShowNoRefOnly(!showNoRefOnly)}
            className={`px-4 py-2 rounded-xl font-bold flex items-center justify-center gap-2 transition-all text-sm border ${showNoRefOnly ? 'bg-amber-500/20 text-amber-500 border-amber-500/50' : 'bg-zinc-800 text-zinc-400 border-transparent hover:bg-zinc-700 hover:text-white'}`}
            title="Mostrar solo asientos sin código de referencia"
          >
            <History size={16} />
            Sin Ref
          </button>
          <button
            onClick={() => setShowUnbalancedOnly(!showUnbalancedOnly)}
            className={`px-4 py-2 rounded-xl font-bold flex items-center justify-center gap-2 transition-all text-sm border ${showUnbalancedOnly ? 'bg-red-500/20 text-red-500 border-red-500/50' : 'bg-zinc-800 text-zinc-400 border-transparent hover:bg-zinc-700 hover:text-white'}`}
          >
            <Filter size={16} />
            Desbalanceados
          </button>
          <button 
            onClick={() => { setEditingEntry(null); setShowForm(true); }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl font-bold flex items-center justify-center gap-2 transition-all"
          >
            <Plus size={18} />
            Nuevo Asiento
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-zinc-950/50 text-[11px] font-bold text-zinc-500 uppercase tracking-widest border-b border-zinc-800">
              <th className="px-6 py-4 w-32">Fecha</th>
              <th className="px-6 py-4">Concepto / Cuentas</th>
              <th className="px-6 py-4 text-right w-32">Debe</th>
              <th className="px-6 py-4 text-right w-32">Haber</th>
              <th className="px-6 py-4 w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {filteredEntries.map((entry) => {
              const unbalance = isEntryUnbalanced(entry);
              return (
                <tr key={entry.id} className={`group transition-all ${unbalance ? 'bg-red-500/10 hover:bg-red-500/20' : 'hover:bg-zinc-800/20'}`}>
                  <td className="px-6 py-6 align-top">
                    <div className="text-sm font-bold text-white">
                      {safeFormat(entry.date, 'dd/MM/yyyy')}
                    </div>
                    <div className="flex flex-col gap-1.5 mt-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[10px] text-indigo-400 bg-indigo-500/10 px-1.5 py-0.5 rounded font-mono font-bold" title="ID de Asiento">
                          #{entry.id.slice(-4).toUpperCase()}
                        </span>
                        {entry.documentLocatorCode ? (
                          <span className="text-[10px] font-mono font-bold bg-white text-black px-1.5 py-0.5 rounded tracking-widest shadow-lg shadow-white/5" title="Código de Referencia del Documento">
                            REF: {entry.documentLocatorCode}
                          </span>
                        ) : (
                          <span className="text-[9px] font-bold text-zinc-600 bg-zinc-800/50 px-1 py-0.5 rounded uppercase tracking-tighter" title="Sin código de referencia vinculada">
                            S/Ref
                          </span>
                        )}
                      </div>
                      <div className="text-[9px] text-zinc-500 uppercase font-bold tracking-widest flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-700"></span>
                        {entry.type}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-6 align-top">
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`text-sm font-bold ${unbalance ? 'text-red-400' : 'text-indigo-400'}`}>
                        {entry.description}
                      </div>
                      {unbalance && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/20 text-red-500 border border-red-500/50">
                          DESBALANCEADO
                        </span>
                      )}
                    </div>
                    <div className="space-y-2">
                    {entry.lines.map((line, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs">
                        <span className={`font-mono ${line.debit > 0 ? 'text-zinc-300' : 'text-zinc-500 pl-8'}`}>
                          {getAccountName(line.accountId)}
                        </span>
                        <div className="flex gap-12 w-64 justify-end font-mono">
                          <span className={`w-32 text-right ${line.debit > 0 ? 'text-emerald-400' : 'text-transparent'}`}>
                            {line.debit > 0 ? formatCurrency(line.debit) : '-'}
                          </span>
                          <span className={`w-32 text-right ${line.credit > 0 ? 'text-red-400' : 'text-transparent'}`}>
                            {line.credit > 0 ? formatCurrency(line.credit) : '-'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </td>
                <td colSpan={2}></td>
                <td className="px-6 py-6 align-top text-right">
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity justify-end">
                    <button 
                      onClick={() => { setEditingEntry(entry); setShowForm(true); }}
                      className="p-1.5 text-zinc-500 hover:text-white transition-colors"
                    >
                      <Edit2 size={14} />
                    </button>
                    {deletingId === entry.id ? (
                      <div className="flex items-center bg-red-500/10 rounded-lg overflow-hidden border border-red-500/20">
                        <button onClick={() => handleDelete(entry.id)} className="px-2 py-1 text-[10px] font-bold text-red-400 hover:bg-red-500 hover:text-black">
                          SI
                        </button>
                        <button onClick={() => setDeletingId(null)} className="px-2 py-1 text-zinc-400">
                          X
                        </button>
                      </div>
                    ) : (
                      <button 
                        onClick={() => setDeletingId(entry.id)}
                        className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
            {filteredEntries.length === 0 && (
              <tr>
                <td colSpan={5} className="py-20 text-center text-zinc-500">
                  No se encontraron asientos contables.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <EntryFormModal 
          onClose={() => setShowForm(false)} 
          userId={userId} 
          accounts={accounts} 
          editingEntry={editingEntry || undefined}
        />
      )}
    </div>
  );
};
